import React, { memo, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { Animated, DeviceEventEmitter, Dimensions, Pressable, StyleSheet, Text, View } from 'react-native';
import { getCleanUrl, isWeb } from '../../utils/Helper';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { ImageComp } from '../../components/Image/Image';
import HomePage from './HomePage';
import { setCurrentHomePageWidgetList } from '../../Redux/Reducers/deviceConfigReducer';
import { useNavigation } from '@react-navigation/native';
import HotelListWidget from '../selectLocation/hotelListWidget';
import { appLogo, chatKeys, checkOutKey, DEVICEREGISTRATIONENABLED, doorKey, ISINROOMTAB, locationSelectorWidget, myStayTab, notificationKey, tabphone } from '../../config/Constant';
import { useCssStyle } from '../../customHook/getCssStyle';
import { handleOpenChat } from '../chat/initializeChat';
import { setGoBackTab, setGoHome, setRenderAdminModal, setShowLoginModal, setShowOfflineMessage } from '../../Redux/Reducers/appConfigReducer';
import { DOOR_KEY_FLOW, NOTIFICATION } from '../../navigation/StackNavigator/routes';
import triggerHapticFeedback from '../../js/vibration';
import { useOrientation, isLandscape } from '../../customHook/useOrientation';

import { TabBar } from './tabbar/index.js';
import FloatingWidget from '../../components/widgetTypes/floatingWidget';
import Notification from '../notification/notification';
import BookmarkPopup from '../../components/bookmarkPopup';
import { setShowSoftphone } from '../../Redux/Reducers/softphoneReducer';
import StackNavigatorLandscape from '../../navigation/StackNavigator/stackNavigatorLandscape';
import { MyBill } from '../myBill/index.js';

// Bottom Tab Navigator
const Tab = createBottomTabNavigator();

const MainLayout = () => {


  const navigation = useNavigation();
  const dispatch = useDispatch();
  const getStyles = useCssStyle();

  const { goBackTab } = useSelector(state => state.appConfigSlice);

  const tabNavigationRef = useRef(null);
  const previousTabRef = useRef(null);
  const currentTabRef = useRef(null);

  const tapCountRef = useRef(0);
  const tapTimerRef = useRef(null);

  // Combine selectors to reduce re-renders
  const {
    appOrientation,
    isOnline,
    isLoggedIn,
    redirectToTabId,
    isBookingFound,
    chatInterface,
    webAppWidgetsInfo,
    currentLayout,
    showBookmarkPopup
  } = useSelector(state => ({
    appOrientation: state.appConfigSlice.appOrientation,
    isOnline: state.appConfigSlice.isOnline,
    isLoggedIn: state.appConfigSlice.isLoggedIn,
    redirectToTabId: state.appConfigSlice.redirectToTabId,
    isBookingFound: state.checkInSlice.bookingStatus.isBookingFound,
    chatInterface: state.appPreservedConfigSlice.chatInterface,
    webAppWidgetsInfo: state.deviceConfigSlice.webAppWidgetsInfo,
    currentLayout: state.deviceConfigSlice.currentLayout,
    showBookmarkPopup: state.appConfigSlice.showBookmarkPopup
  }), shallowEqual);

  const tabDetails = currentLayout?.tabDetails || [];
  const widgetList = useMemo(() => {
    return currentLayout?.scrollableWidgets ?? [];
  }, [currentLayout]);

  const [initialTab, setInitialTab] = useState({});




  const handleFooterNavClick = (tab, bottomTabNavigation) => {
    
    triggerHapticFeedback('effectTick');

    if (chatKeys.includes(tab.uniqueKey)) {

      if(!isOnline) {
          dispatch(setShowOfflineMessage(true));
          return;
      }

      dispatch(handleOpenChat());
      return;
    }

    if (doorKey.includes(tab.uniqueKey)) {

      if(!isOnline) {
        dispatch(setShowOfflineMessage(true));
        return;
      }

      navigation.navigate(DOOR_KEY_FLOW)
      return;
    }

    if(appLogo.includes(tab.uniqueKey)){
      const handleDeviceAdminPage = () => {
        if(!ISINROOMTAB && !DEVICEREGISTRATIONENABLED){
          return;
        }
    
        tapCountRef.current += 1;
    
        // always clear any existing timer
        if (tapTimerRef.current) {
          clearTimeout(tapTimerRef.current);
        }
        
        // if user hits 5 taps, trigger the modal and reset
        if (tapCountRef.current === 5) {
          tapCountRef.current = 0;
          dispatch(setRenderAdminModal(true))
        }
    
        // otherwise, reset tap count after 2 seconds
        tapTimerRef.current = setTimeout(() => {
          tapCountRef.current = 0;
        }, 2000);
      }

      handleDeviceAdminPage();
      return
    }

    if (tabphone.includes(tab.uniqueKey)) {

      if(!isOnline) {
        dispatch(setShowOfflineMessage(true));
        return;
      }

      dispatch(setShowSoftphone(true));
      return;
    }

    const currentRoute = bottomTabNavigation.getState().routes[bottomTabNavigation.getState().index];

    if (currentRoute.name === tab.uniqueKey) {
      // Tab is already focused, trigger scrollToTop
      DeviceEventEmitter.emit('scrollToTop', tab);
      dispatch(setGoHome(true));
    } else {
      // Tab is not focused, navigate to it
      previousTabRef.current = currentRoute.name;
      currentTabRef.current = tab.uniqueKey;
      bottomTabNavigation.navigate(tab.uniqueKey);
    }
  };

  const getDefaultFooter = () => {
    let defaultTab = tabDetails[0];

    if (tabDetails?.length > 0) {
    if (redirectToTabId) {
      // Find the tab with the uniqueKey that matches initialTabId
      const requiredTabItem = tabDetails.find(tabItem =>
        redirectToTabId.includes(tabItem.uniqueKey.replace(/ /g, '').toLowerCase())
      );
      if (requiredTabItem) {
        defaultTab = requiredTabItem;
      }
    } else if (isLoggedIn || isBookingFound) {
      // Find the "my stay" tab
      const myStayTabItem = tabDetails.find(tabItem =>
        myStayTab.includes(tabItem.uniqueKey.replace(/ /g, '').toLowerCase())
      );

      if (myStayTabItem) {
        defaultTab = myStayTabItem;
      }
    }

    console.log(defaultTab);
    setInitialTab(defaultTab);
    }
  };

  useEffect(() => {    
    // select required footer tab
    if(tabDetails?.length){
      getDefaultFooter();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabDetails]);

  const handleGoBackTab = () => {
    dispatch(setGoBackTab(false))

    const tabNavigation = tabNavigationRef.current;
    if (!tabNavigation) return;

    const tabState = tabNavigation?.getState();

    if(tabNavigation?.canGoBack()){
      tabNavigation?.goBack();
      return
    } 
    const targetTab = previousTabRef.current ?? initialTab?.uniqueKey;
    const currentRoute = tabState.routes[tabState.routes?.length - 1]?.name;
    if (currentRoute === targetTab) {
      console.log(`Already on ${targetTab}, no navigation needed.`);
      return;
    }
    
    previousTabRef.current = null;
    currentTabRef.current = null;

    tabNavigation.reset({
      index: 0,
      routes: [{ name: targetTab }],
    });
  }

  useEffect(() => {
    if(goBackTab){
      handleGoBackTab()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goBackTab])


  const GetTabComp = useCallback((tabData) => {
    const matchedWidgetList = widgetList.find((item) => item.uniqueKey == tabData.uniqueKey);
    const isLandscapeMode = isLandscape(appOrientation);

    // Determine tab content based on tab type
    let tabContent;
    if (locationSelectorWidget.some(el => el === tabData.uniqueKey)) {
      tabContent = <HotelListWidget hideBackButton={true} tabDetails={tabData} />;
    } else if (notificationKey.some(el => el === tabData.uniqueKey)) {
      tabContent = <Notification hideBackButton={true} tabDetails={tabData} />;
    } else if (checkOutKey.some(el => el === tabData.uniqueKey)){
      tabContent = <MyBill isTabScreen={true} tabDetails={tabData} />
    } else {
      tabContent = <HomePage widgetList={matchedWidgetList?.widgets} tabDetails={tabData} />;
    }

    // In landscape mode, wrap each tab with its own stack navigator to keep tabbar visible during navigation
    // Each tab maintains its own navigation stack, allowing navigation within the tab while keeping the tabbar visible
    if (isLandscapeMode) {
      return (
        <StackNavigatorLandscape tabKey={tabData.uniqueKey}>
          {tabContent}
        </StackNavigatorLandscape>
      );
    }

    return tabContent;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgetList, appOrientation]);

  // Memoize TabBar component to prevent unnecessary re-renders
  const renderTabBar = useCallback((props) => {
    tabNavigationRef.current = props.navigation;
    return <TabBar {...props} handleFooterNavClick={handleFooterNavClick} />

    // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  return (
    <>
    {currentLayout?.floatingWidgets?.length > 0 && <FloatingWidget listItemData={currentLayout.floatingWidgets} />}
    {Object.keys(initialTab)?.length > 0 && (
      <Tab.Navigator
        backBehavior="none"
        key={initialTab.uniqueKey}
        initialRouteName={initialTab.uniqueKey}
        tabBar={renderTabBar}
        screenOptions={{
          headerShown: false,
          freezeOnBlur: true,
          detachInactiveScreens: false,
          tabBarPosition: isLandscape(appOrientation) ? "left" : "bottom",
        }}
      >
        {tabDetails.map((tab, index) => {

          if (chatKeys.includes(tab.uniqueKey) && !chatInterface.chatInitialized) {
            return null;
          }
          return (
            <Tab.Screen
              key={tab.uniqueKey}
              name={tab.uniqueKey}
              children={() => GetTabComp(tab)}
            />
          );
        })}
      </Tab.Navigator>
    )}
    {showBookmarkPopup && <BookmarkPopup />}
    </>
  );
};


export default memo(MainLayout);
